import type { CompiledCollider } from '../../chunk-schema/src/index.ts'

export interface BlockingStructureSegment {
  readonly id: string
  readonly ax: number
  readonly az: number
  readonly bx: number
  readonly bz: number
  readonly minY: number
  readonly maxY: number
  readonly minX: number
  readonly minZ: number
  readonly maxX: number
  readonly maxZ: number
}

export interface ResolvedStructureMotion {
  readonly x: number
  readonly z: number
  readonly collided: boolean
  readonly colliderId?: string
}

const VERTICAL_NORMAL_Y_LIMIT = 0.45
const POSITION_QUANTISATION = 1_000

/**
 * Extracts only the near-vertical faces which can block a car laterally.
 * Horizontal deck, tunnel-ceiling and road triangles remain height authority;
 * treating their triangulation edges as walls would strand the car on them.
 */
export function createBlockingStructureSegments(
  colliders: readonly CompiledCollider[],
  offsetX = 0,
  offsetZ = 0,
): readonly BlockingStructureSegment[] {
  const segments = new Map<string, BlockingStructureSegment>()
  for (const collider of colliders) {
    if (collider.kind !== 'trimesh' || collider.material === 'asphalt') continue
    for (let index = 0; index < collider.indices.length; index += 3) {
      const a = point(collider.positions, collider.indices[index], offsetX, offsetZ)
      const b = point(collider.positions, collider.indices[index + 1], offsetX, offsetZ)
      const c = point(collider.positions, collider.indices[index + 2], offsetX, offsetZ)
      if (!a || !b || !c) continue
      const ab = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }
      const ac = { x: c.x - a.x, y: c.y - a.y, z: c.z - a.z }
      const nx = ab.y * ac.z - ab.z * ac.y
      const ny = ab.z * ac.x - ab.x * ac.z
      const nz = ab.x * ac.y - ab.y * ac.x
      const normalLength = Math.hypot(nx, ny, nz)
      if (normalLength < 1e-8 || Math.abs(ny / normalLength) > VERTICAL_NORMAL_Y_LIMIT) continue
      const minY = Math.min(a.y, b.y, c.y)
      const maxY = Math.max(a.y, b.y, c.y)
      for (const [start, end] of [[a, b], [b, c], [c, a]] as const) {
        if (Math.hypot(end.x - start.x, end.z - start.z) < 0.01) continue
        const first = quantisedPoint(start.x, start.z)
        const second = quantisedPoint(end.x, end.z)
        const ordered = first < second
          ? { ax: start.x, az: start.z, bx: end.x, bz: end.z, key: `${first}>${second}` }
          : { ax: end.x, az: end.z, bx: start.x, bz: start.z, key: `${second}>${first}` }
        const key = `${collider.id}|${ordered.key}|${Math.round(minY * POSITION_QUANTISATION)}|${Math.round(maxY * POSITION_QUANTISATION)}`
        if (segments.has(key)) continue
        segments.set(key, {
          id: collider.id,
          ax: ordered.ax,
          az: ordered.az,
          bx: ordered.bx,
          bz: ordered.bz,
          minY,
          maxY,
          minX: Math.min(ordered.ax, ordered.bx),
          minZ: Math.min(ordered.az, ordered.bz),
          maxX: Math.max(ordered.ax, ordered.bx),
          maxZ: Math.max(ordered.az, ordered.bz),
        })
      }
    }
  }
  return [...segments.values()].sort((left, right) =>
    left.id.localeCompare(right.id, 'en') || left.ax - right.ax || left.az - right.az || left.bx - right.bx || left.bz - right.bz
  )
}

/** Returns only segments which can touch the requested swept prism. */
export function blockingSegmentsNear(
  segments: readonly BlockingStructureSegment[],
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
  minY: number,
  maxY: number,
): readonly BlockingStructureSegment[] {
  return segments.filter((segment) =>
    segment.maxX >= minX && segment.minX <= maxX &&
    segment.maxZ >= minZ && segment.minZ <= maxZ &&
    segment.maxY >= minY && segment.minY <= maxY
  )
}

/**
 * Sweeps a circular vehicle footprint through the vertical faces. Sampling is
 * bounded by a quarter radius and the first contact is binary-refined, so a
 * thin wall cannot be skipped by a long frame step.
 */
export function resolveMotionAgainstBlockingSegments(
  startX: number,
  startZ: number,
  endX: number,
  endZ: number,
  minY: number,
  maxY: number,
  radiusMeters: number,
  segments: readonly BlockingStructureSegment[],
): ResolvedStructureMotion {
  const radius = Math.max(0.01, radiusMeters)
  const dx = endX - startX
  const dz = endZ - startZ
  const distance = Math.hypot(dx, dz)
  if (distance < 1e-8 || segments.length === 0) return { x: endX, z: endZ, collided: false }
  const candidates = blockingSegmentsNear(
    segments,
    Math.min(startX, endX) - radius,
    Math.min(startZ, endZ) - radius,
    Math.max(startX, endX) + radius,
    Math.max(startZ, endZ) + radius,
    minY,
    maxY,
  )
  if (candidates.length === 0) return { x: endX, z: endZ, collided: false }

  const blockedAt = (t: number): BlockingStructureSegment | undefined => {
    const x = startX + dx * t
    const z = startZ + dz * t
    return candidates.find((segment) => pointSegmentDistanceSquared(x, z, segment) <= radius * radius)
  }
  // If recovery/spawn leaves the centre overlapping a wall, allow motion out
  // of it. Once clear, the same face becomes blocking again.
  let wasBlocked = blockedAt(0) !== undefined
  const steps = Math.max(1, Math.ceil(distance / Math.max(0.05, radius * 0.25)))
  let previousT = 0
  for (let step = 1; step <= steps; step += 1) {
    const t = step / steps
    const blocker = blockedAt(t)
    if (blocker === undefined) {
      wasBlocked = false
      previousT = t
      continue
    }
    if (wasBlocked) {
      previousT = t
      continue
    }
    let low = previousT
    let high = t
    for (let iteration = 0; iteration < 14; iteration += 1) {
      const middle = (low + high) * 0.5
      if (blockedAt(middle) === undefined) low = middle
      else high = middle
    }
    // A millimetre-scale retreat avoids floating-point recontact next frame.
    const retreat = Math.min(low, 0.002 / distance)
    const resolvedT = Math.max(0, low - retreat)
    return {
      x: startX + dx * resolvedT,
      z: startZ + dz * resolvedT,
      collided: true,
      colliderId: blocker.id,
    }
  }
  return { x: endX, z: endZ, collided: false }
}

function point(
  positions: ArrayLike<number>,
  vertexIndex: number | undefined,
  offsetX: number,
  offsetZ: number,
): { readonly x: number; readonly y: number; readonly z: number } | undefined {
  if (vertexIndex === undefined || !Number.isInteger(vertexIndex) || vertexIndex < 0) return undefined
  const offset = vertexIndex * 3
  if (offset + 2 >= positions.length) return undefined
  const x = positions[offset] + offsetX
  const y = positions[offset + 1]
  const z = positions[offset + 2] + offsetZ
  return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z) ? { x, y, z } : undefined
}

function quantisedPoint(x: number, z: number): string {
  return `${Math.round(x * POSITION_QUANTISATION)}|${Math.round(z * POSITION_QUANTISATION)}`
}

function pointSegmentDistanceSquared(
  x: number,
  z: number,
  segment: BlockingStructureSegment,
): number {
  const dx = segment.bx - segment.ax
  const dz = segment.bz - segment.az
  const lengthSquared = dx * dx + dz * dz
  const t = lengthSquared < 1e-12
    ? 0
    : Math.max(0, Math.min(1, ((x - segment.ax) * dx + (z - segment.az) * dz) / lengthSquared))
  const nearestX = segment.ax + dx * t
  const nearestZ = segment.az + dz * t
  return (x - nearestX) ** 2 + (z - nearestZ) ** 2
}
